if (!customElements.get('sticky-atc-bar')) {
  customElements.define(
    'sticky-atc-bar',
    class StickyAtcBar extends HTMLElement {
      constructor() {
        super();
        document.body.classList.add('sticky-atc-bar-enabled');

        this.selectors = {
          variantIdSelect: '[name="id"]',
        };
        this.refreshToken = 0;
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
      get productId() {
        return this.getAttribute('data-product-id');
      }
      get select() {
        return this.querySelector(this.selectors.variantIdSelect);
      }
      get productForm() {
        return this.querySelector('form[is="product-form"]');
      }
      get submitButton() {
        return this.querySelector('[type="submit"][name="add"]');
      }

      connectedCallback() {
        this.productFormActions = document.querySelector(`.main-product-form[data-product-id="${this.productId}"]`);
        this.mainProductInfo = document.querySelector(`product-info[data-product-id="${this.productId}"]`);
        this.container = this.closest('.sticky-atc-bar');

        this.variantData = this.getVariantData();

        this.init();
        this.initVariantSelect();
        this.initSheet();

        const hasRequiredFields = this.validateMainProductRequiredFields();
        if (hasRequiredFields && this.submitButton) {
          this.submitButton.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.productFormActions.scrollIntoView({ behavior: 'smooth', block: 'center' });
            setTimeout(() => {
              this.productFormActions.requestSubmit();
            }, 300);
          });
        }
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
        document.removeEventListener('click', this.onDocumentClick);
        document.removeEventListener('keydown', this.onKeydown);
      }

      /* ------------------------------------------------------------- setup */

      init() {
        if (!this.productFormActions) {
          this.container.classList.add('sticky-atc-bar--show');
          return;
        }

        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        mql.onchange = this.checkDevice.bind(this);
        this.checkDevice();

        const rootMargin = `-80px 0px 0px 0px`;
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const method = entry.intersectionRatio !== 1 ? 'add' : 'remove';
              this.container.classList[method]('sticky-atc-bar--show');
            });
          },
          { threshold: 1, rootMargin }
        );
        this.observer.observe(this.productFormActions);
        this.syncWithMainProductForm();
      }

      initVariantSelect() {
        const select = this.select;
        if (!select) return;

        select.addEventListener('change', () => {
          if (this.isUpdating) return;
          this.isUpdating = true;

          const selectedVariantId = Number(select.value);
          this.currentVariant = this.variantData.find((variant) => variant.id === selectedVariantId);

          this.mirrorToMainProductForm();
          this.refresh(selectedVariantId);
        });
      }

      getVariantData() {
        if (this.variantData) return this.variantData;
        const json = this.container && this.container.querySelector('[type="application/json"]');
        try {
          this.variantData = json ? JSON.parse(json.textContent) : [];
        } catch (error) {
          console.error('sticky-atc-bar: could not parse variant data', error);
          this.variantData = [];
        }
        return this.variantData;
      }

      checkDevice() {
        document.documentElement.style.setProperty('--sticky-atc-bar-height', this.clientHeight + 'px');
      }

      validateMainProductRequiredFields() {
        if (!this.mainProductInfo || !this.productFormActions) return false;
        return this.mainProductInfo.querySelectorAll('[data-custom-property]').length > 0;
      }

      /* ------------------------------------------------------------ refresh */

      /**
       * Re-renders the price, quantity and button straight from the section so the
       * bar can never drift from the product page. Replaces the old approach of
       * patching individual price nodes by hand, which dropped the <s> wrapper on
       * the compare-at price and never updated the discount badge.
       */
      refresh(variantId) {
        if (!variantId) return Promise.resolve();

        const token = ++this.refreshToken;
        const url = `${this.dataset.url}?variant=${variantId}&section_id=${this.sectionId}`;

        return fetch(url)
          .then((response) => response.text())
          .then((responseText) => {
            if (token !== this.refreshToken) return;

            const parsed = new DOMParser().parseFromString(responseText, 'text/html');
            ['.sticky-atc-bar__price', `#QuantitySticky-${this.sectionId}`].forEach((selector) => {
              const target = this.querySelector(selector);
              const source = parsed.querySelector(selector);
              if (target && source) target.innerHTML = source.innerHTML;
            });

            const source = parsed.querySelector('[type="submit"][name="add"]');
            if (source && this.submitButton) {
              this.submitButton.toggleAttribute('disabled', source.hasAttribute('disabled'));
              const label = this.submitButton.querySelector('span');
              const newLabel = source.querySelector('span');
              if (label && newLabel) label.textContent = newLabel.textContent;
            }
          })
          .catch((error) => console.error('sticky-atc-bar: refresh failed', error))
          .finally(() => {
            if (token === this.refreshToken) this.isUpdating = false;
          });
      }

      /** Mirrors the bar's selection onto the main product form's variant picker. */
      mirrorToMainProductForm() {
        const mainVariantSelects = this.mainProductInfo && this.mainProductInfo.querySelector('variant-selects');
        if (!mainVariantSelects || !this.currentVariant) return;

        Array.from(mainVariantSelects.querySelectorAll('select, fieldset')).forEach((element, index) => {
          const variantOptionVal = this.currentVariant.options[index];
          if (variantOptionVal === undefined) return;

          switch (element.tagName) {
            case 'SELECT': {
              element.querySelectorAll('option').forEach((option) => option.removeAttribute('selected'));
              element.value = variantOptionVal;
              const selectedOption = element.querySelector(`option[value="${variantOptionVal}"]`);
              if (selectedOption) selectedOption.setAttribute('selected', 'selected');
              break;
            }
            case 'FIELDSET':
              element.querySelectorAll('input').forEach((radio) => {
                if (radio.value === variantOptionVal) radio.checked = true;
              });
              break;
          }
        });

        setTimeout(() => {
          mainVariantSelects.dispatchEvent(new Event('change', { detail: { formStickty: true } }));
        }, 0);
      }

      syncWithMainProductForm() {
        FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, (event) => {
          if (!this.mainProductInfo) return;
          if (event.data.sectionId !== this.mainProductInfo.dataset.section) return;

          const variant = event.data.variant;
          if (!variant) return;

          this.currentVariant = variant;
          const select = this.select;
          if (select) select.value = variant.id;

          this.refresh(variant.id);
        });
      }

      /* -------------------------------------------------------------- sheet */

      initSheet() {
        this.sheet = this.querySelector('.sticky-atc-bar__sheet');
        if (!this.sheet) return;

        this.sheetTriggers = Array.from(this.querySelectorAll('[data-sticky-sheet-open]'));
        this.sheetTriggers.forEach((trigger) => {
          trigger.addEventListener('click', (event) => {
            event.preventDefault();
            this.toggleSheet(this.sheet.hasAttribute('hidden'));
          });
        });

        this.querySelectorAll('[data-sticky-sheet-close]').forEach((button) => {
          button.addEventListener('click', () => this.toggleSheet(false));
        });

        this.onDocumentClick = (event) => {
          if (this.sheet.hasAttribute('hidden')) return;
          if (event.target.closest('.sticky-atc-bar__sheet, [data-sticky-sheet-open]')) return;
          this.toggleSheet(false);
        };
        this.onKeydown = (event) => {
          if (event.key === 'Escape') this.toggleSheet(false);
        };

        document.addEventListener('click', this.onDocumentClick);
        document.addEventListener('keydown', this.onKeydown);
      }

      toggleSheet(open) {
        if (!this.sheet) return;
        this.sheet.toggleAttribute('hidden', !open);
        this.sheetTriggers.forEach((trigger) => trigger.setAttribute('aria-expanded', String(open)));
      }
    }
  );
}
